import { gql, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from 'hephaestus/web';
import { Refresh } from 'hephaestus/webIcons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';

const TRIGGER_SYNCHRONOUS_INTEGRATION = gql`
  mutation triggerSynchronousIntegration(
    $input: TriggerSynchronousIntegrationInput!
  ) {
    triggerSynchronousIntegration(input: $input) {
      workflowCompletion {
        id
      }
    }
  }
`;

export const SynchronousIntegration = ({
  onNext,
  onBack,
  workflowCompletion,
  subStepId,
  options,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  subStepId: string;
  options: any;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [triggerIntegration] = useMutation(TRIGGER_SYNCHRONOUS_INTEGRATION, {
    onCompleted: () => {
      setError(null);
      if (onNext) {
        onNext();
      }
    },
  });

  const attemptIntegration = async () => {
    setIsLoading(true);
    setError(null);

    const maxAttempts = options.allowAutoRetry ? 3 : 1;
    for (let attempt = 1; attempt <= maxAttempts; ++attempt) {
      try {
        await triggerIntegration({
          variables: {
            input: {
              workflowCompletionId: workflowCompletion.id,
              subStepId,
            },
            skipErrorHandler: true,
          },
        });
        break;
      } catch (err) {
        if (attempt === maxAttempts) {
          setError(err as Error);
          setIsLoading(false);
        }
      }
    }

    setIsLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: integrations initially fire on mount
  useEffect(() => {
    // When navigating backward, skip the integration and continue going back
    if (direction === 'BACKWARD') {
      onBack();
      return;
    }
    attemptIntegration();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        minHeight: '300px',
      }}
    >
      <Typography variant='headingMedium'>
        {translateBackendString(options.title) ||
          t('synchronousIntegration.title')}
      </Typography>
      {options.subtitle && (
        <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
          {translateBackendString(options.subtitle)}
        </Typography>
      )}
      {error && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Alert severity='error'>{error.message}</Alert>
          {options.allowManualRetry && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                onClick={attemptIntegration}
                disabled={isLoading}
                variant='text'
                startIcon={<Refresh />}
              >
                {t('shared:retry')}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
